import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { Zap, MessageCircle, Copy, FileDown, ArrowRight, PartyPopper } from "lucide-react";

const WHATSAPP_NUMBER = "918722915521";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ROLES = [
  { v: "customer", label: "Existing Customer" },
  { v: "sse", label: "Sales & Service Executive" },
  { v: "influencer", label: "Influencer / Partner" },
];

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", role: "customer" });
  const [busy, setBusy] = useState(false);
  const [welcome, setWelcome] = useState(null); // user after registration

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await register(form);
      toast.success(`Welcome ${u.name}!`);
      setWelcome(u);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Registration failed");
    } finally {
      setBusy(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target?.value ?? e });

  const referralLink = welcome ? `${window.location.origin}/register?ref=${welcome.referral_code}` : "";
  const posterUrl = welcome ? `${BACKEND_URL}/api/poster/${welcome.referral_code}.png` : "";

  const openWhatsAppSelf = () => {
    if (!welcome) return;
    const text = `🎉 I just joined the Shri Shanteshwar Enterprises Rosen E-Bikes Referral Program!\n\n` +
      `My code: *${welcome.referral_code}*\n` +
      `Share this link: ${referralLink}\n` +
      `Print my poster: ${posterUrl}\n\n` +
      `Earn cash for every EV I help sell 🏍️⚡`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
  };

  const openWhatsAppOwner = () => {
    if (!welcome) return;
    const text = `Hi Shri Shanteshwar Enterprises! I just joined the SSE Referral Program.\n` +
      `Name: ${welcome.name}\n` +
      `Code: ${welcome.referral_code}\n` +
      `Please share any getting-started tips.`;
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(text)}`, "_blank");
  };

  const copyCode = () => {
    navigator.clipboard.writeText(welcome.referral_code);
    toast.success("Code copied");
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex flex-col md:flex-row">
      <div className="hidden md:flex md:w-1/2 relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1654748646458-056253a82853?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwzfHxlbGVjdHJpYyUyMHNjb290ZXJ8ZW58MHx8fHwxNzg2NjMzNTI3fDA&ixlib=rb-4.1.0&q=85"
          alt="" className="w-full h-full object-cover opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-transparent to-transparent" />
        <div className="absolute bottom-12 left-12 right-12">
          <Link to="/" className="inline-flex items-center gap-2 mb-8">
            <span className="w-9 h-9 rounded-lg bg-[#00FF66] flex items-center justify-center">
              <Zap className="w-5 h-5 text-black" strokeWidth={2.5} />
            </span>
            <div className="font-display font-black">SHRI SHANTESHWAR</div>
          </Link>
          <h2 className="font-display font-black text-4xl leading-tight">
            Turn every EV recommendation into <span className="text-[#00FF66]">real cash</span>.
          </h2>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 md:p-10 overflow-y-auto">
        <Card className="w-full max-w-md bg-[#111111] border-white/10 p-8">
          <div className="mb-6">
            <div className="text-xs uppercase tracking-[0.3em] text-[#00FF66] font-bold mb-2">Create account</div>
            <h1 className="font-display font-black text-3xl">Join the program</h1>
          </div>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-white/70 text-xs uppercase tracking-[0.2em]">Full name</Label>
              <Input required value={form.name} onChange={set("name")} data-testid="register-name" className="mt-2 bg-black/40 border-white/10 h-11" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-white/70 text-xs uppercase tracking-[0.2em]">Email</Label>
                <Input type="email" required value={form.email} onChange={set("email")} data-testid="register-email" className="mt-2 bg-black/40 border-white/10 h-11" />
              </div>
              <div>
                <Label className="text-white/70 text-xs uppercase tracking-[0.2em]">Phone</Label>
                <Input required value={form.phone} onChange={set("phone")} data-testid="register-phone" className="mt-2 bg-black/40 border-white/10 h-11" />
              </div>
            </div>
            <div>
              <Label className="text-white/70 text-xs uppercase tracking-[0.2em]">I am a</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="register-role" className="mt-2 bg-black/40 border-white/10 h-11">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#111] border-white/10 text-white">
                  {ROLES.map((r) => (
                    <SelectItem key={r.v} value={r.v} data-testid={`register-role-${r.v}`}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-white/70 text-xs uppercase tracking-[0.2em]">Password</Label>
              <Input type="password" required minLength={6} value={form.password} onChange={set("password")} data-testid="register-password" className="mt-2 bg-black/40 border-white/10 h-11" />
            </div>
            <Button type="submit" disabled={busy} className="w-full rounded-full bg-[#00FF66] text-black hover:bg-[#00E55C] font-semibold h-11" data-testid="register-submit">
              {busy ? "Creating account…" : "Create account"}
            </Button>
          </form>
          <div className="mt-6 text-sm text-white/50">
            Already registered?{" "}
            <Link to="/login" className="text-[#00FF66] hover:underline" data-testid="register-to-login">Log in</Link>
          </div>
        </Card>
      </div>

      {/* Welcome kit dialog */}
      <Dialog open={!!welcome} onOpenChange={(o) => { if (!o) { setWelcome(null); nav("/dashboard"); } }}>
        <DialogContent className="bg-[#111] border-[#00FF66]/30 text-white sm:max-w-lg" data-testid="welcome-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-3xl flex items-center gap-2">
              <PartyPopper className="w-6 h-6 text-[#00FF66]" /> You're in!
            </DialogTitle>
            <p className="text-xs text-white/60 mt-1">Send your welcome kit to WhatsApp — start referring in the next 30 seconds.</p>
          </DialogHeader>
          {welcome && (
            <div className="space-y-4">
              <div className="p-5 rounded-xl bg-gradient-to-br from-[#00FF66]/10 to-[#111] border border-[#00FF66]/30 text-center">
                <div className="text-xs uppercase tracking-[0.3em] text-[#00FF66] font-bold">Your referral code</div>
                <div className="font-display font-black text-4xl text-[#00FF66] mt-2 tracking-wider" data-testid="welcome-code">
                  {welcome.referral_code}
                </div>
                <div className="text-[10px] text-white/40 mt-2 break-all">{referralLink}</div>
              </div>

              <div className="rounded-lg bg-black/30 border border-white/10 p-3 flex items-center gap-3">
                <img src={posterUrl} alt="Your QR poster" className="w-20 h-28 object-cover rounded border border-white/10" data-testid="welcome-poster-thumb" />
                <div className="flex-1 text-xs text-white/60">
                  Your print-ready QR poster is generated. Print it for events or share the link — anyone who scans lands on your referral page.
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <Button onClick={openWhatsAppSelf} className="rounded-full bg-[#25D366] text-black hover:bg-[#20BF5B] font-semibold h-11 col-span-2" data-testid="welcome-wa-self">
                  <MessageCircle className="w-4 h-4 mr-2" /> Send kit to my WhatsApp
                </Button>
                <Button onClick={openWhatsAppOwner} variant="outline" className="rounded-full border-white/20 bg-transparent text-white hover:bg-white/10 hover:text-white h-10" data-testid="welcome-wa-owner">
                  <MessageCircle className="w-4 h-4 mr-2" /> WhatsApp Us
                </Button>
                <Button onClick={copyCode} variant="outline" className="rounded-full border-white/20 bg-transparent text-white hover:bg-white/10 hover:text-white h-10" data-testid="welcome-copy-code">
                  <Copy className="w-4 h-4 mr-2" /> Copy code
                </Button>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => { setWelcome(null); nav("/dashboard"); }} className="rounded-full bg-[#00FF66] text-black hover:bg-[#00E55C] font-semibold" data-testid="welcome-continue">
              Go to dashboard <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
